import TaskSource from '../components/TaskSource';
import { can } from '../utils/memberPermissions';
import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, CheckCircle, AlertCircle, Clock, ChevronRight } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboardAPI, followUpsAPI } from '../services/api';
import FormError from '../components/FormError';
import { formatFollowUp, followUpShortcut } from '../utils/followUps';
import { useAuth } from '../contexts/AuthContext';
import { getLeadStatus } from '../utils/leadStatuses';

const TodayDashboard = () => {
  const { user } = useAuth();
  const overdueRefs = useRef({});
  const [confirmation, setConfirmation] = useState('');

  const {
    data,
    isLoading: loading,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['dashboard', 'today'],
    staleTime: 0,
    queryFn: async () => {
      const { data } = await dashboardAPI.getToday();
      const formatItem = (item) => ({
        ...item,
        scheduledTime: item.date && item.time ? `${item.date}T${item.time}` : null,
      });
      return {
        ...data,
        today: (data.today || []).map(formatItem),
        overdue: (data.overdue || []).map(formatItem),
        completed: (data.completed || []).map(formatItem),
      };
    },
  });

  const followUps = {
    today: data?.today || [],
    overdue: data?.overdue || [],
  };

  const stats = data?.summary || { todayCount: 0, overdueCount: 0, totalActionRequired: 0 };
  const leadCount = Number.isFinite(data?.leadCount) ? data.leadCount : 0;

  const error = isError ? 'Failed to load dashboard' : null;

  const formatOverdueTime = (scheduledTimeStr) => {
    const today = followUpShortcut(0, user?.timezone);
    const days = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${scheduledTimeStr.slice(0, 10)}T00:00:00Z`)) / 86400000);
    return `${Math.max(1, days)}d`;
  };

  const scrollToOverdue = (followUpId) => {
    const element = overdueRefs.current[followUpId];
    if (element) {
      element.focus({ preventScroll: true });
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  if (isError) {
    return <div className="space-y-3">
      <FormError message="Could not load tasks. Your counts are unavailable." />
      <button type="button" onClick={() => refetch()} disabled={isFetching} className="px-4 py-2 rounded-lg border bg-white text-gray-700">
        {isFetching ? 'Retrying…' : 'Try again'}
      </button>
    </div>;
  }

  if (loading || !data) {
    return (
      <div className="space-y-5" role="status" aria-label="Loading tasks">
        <span className="sr-only">Loading tasks…</span>
        <div className="animate-pulse">
          <div className="h-24 bg-gray-200 rounded-xl"></div>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-8 bg-gray-200 rounded-lg w-32"></div>
          <div className="h-36 bg-gray-200 rounded-xl"></div>
          <div className="h-36 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  const hasFollowUps = followUps.today.length > 0 || followUps.overdue.length > 0;
  const hasLeads = leadCount > 0;

  return (
    <div className="relative space-y-5 lg:space-y-6">
      <p role="status" aria-live="polite" className="text-sm text-green-800">{confirmation}</p>
      <div role="progressbar" aria-label="Updating tasks" aria-hidden={!isFetching}
        className={`pointer-events-none absolute inset-x-0 -top-2 !mt-0 h-0.5 transition-opacity duration-200 motion-reduce:transition-none ${isFetching ? 'opacity-100 delay-300' : 'opacity-0'}`}>
        <div className="h-full rounded-full bg-primary-300 motion-safe:animate-pulse" />
      </div>
      <section aria-label="Task summary" className="bg-white rounded-xl p-4 border border-gray-200">
        <h2 className="font-semibold text-gray-900">
          {stats.totalActionRequired === 0 ? 'No tasks due'
            : stats.totalActionRequired === 1 ? '1 task needs attention'
              : `${stats.totalActionRequired} tasks need attention`}
        </h2>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <p className={stats.overdueCount ? 'text-red-800' : 'text-gray-600'}><strong>{stats.overdueCount}</strong> overdue</p>
          <p className="text-gray-600"><strong>{stats.todayCount}</strong> due today</p>
        </div>
        {hasFollowUps && <p className="mt-2 text-xs text-gray-500">Open an enquiry to log contact, snooze, or reschedule.</p>}
      </section>
      {followUps.overdue.length > 0 && (
        <button type="button" onClick={() => scrollToOverdue(followUps.overdue[0].id)}
          className="w-full flex items-center gap-3 text-left rounded-lg border border-red-200 bg-red-50 p-3 text-red-900 hover:bg-red-100">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span className="min-w-0 flex-1 break-words"><span className="block text-xs font-medium">Oldest overdue task</span>
            <span className="font-semibold">{followUps.overdue[0].leadName}</span> · {formatOverdueTime(followUps.overdue[0].scheduledTime)} overdue
          </span>
          <ChevronRight className="w-5 h-5 shrink-0" />
        </button>
      )}

      {/* Empty State */}
      {!hasFollowUps && !error && (
        <div className="text-center py-16 px-4">
          <div className="bg-green-50 rounded-2xl w-20 h-20 flex items-center justify-center mx-auto mb-6 border border-green-100">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {hasLeads ? 'No tasks due today' : 'Add your first enquiry to get started'}
          </h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            {hasLeads
              ? 'Your scheduled tasks are clear. Check your enquiries for the next opportunity.'
              : 'Create your first enquiry so you can track statuses and set task reminders.'}
          </p>
          <Link
            to="/leads/new"
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-primary-700 active:bg-primary-800 transition-all shadow-sm hover:shadow"
          >
            {hasLeads ? 'Add enquiry' : 'Add enquiry'}
          </Link>
        </div>
      )}

      {(followUps.overdue.length > 0 || followUps.today.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Overdue Tasks */}
          {followUps.overdue.length > 0 && (
            <div>
              <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="bg-red-100 rounded-lg p-1.5">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                </div>
                Overdue
                <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  {followUps.overdue.length}
                </span>
              </h2>
              <div className="space-y-3">
                {followUps.overdue.map((followUp) => (
                  <FollowUpCard
                    key={followUp.id}
                    followUp={followUp}
                    onSuccess={setConfirmation}
                    isOverdue
                    formatOverdueTime={formatOverdueTime}
                    ref={(el) => (overdueRefs.current[followUp.id] = el)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Today's Tasks */}
          {followUps.today.length > 0 && (
            <div>
              <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                <div className="bg-blue-100 rounded-lg p-1.5">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                Today
                <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  {followUps.today.length}
                </span>
              </h2>
              <div className="space-y-3">
                {followUps.today.map((followUp) => (
                  <FollowUpCard
                    key={followUp.id}
                    followUp={followUp}
                    onSuccess={setConfirmation}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {data?.completed?.length > 0 && (
        <details aria-label="completed tasks" className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
          <summary className="min-h-[44px] cursor-pointer text-sm font-semibold text-gray-700">Recently completed ({data.completed.length})</summary>
          <p className="text-sm text-gray-500">Latest 50 completed tasks. Open an enquiry for its full history.</p>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {data.completed.map((followUp) => <FollowUpCard key={followUp.id} followUp={followUp} />)}
          </div>
        </details>
      )}
    </div>
  );
};

const FollowUpCard = React.forwardRef(({ followUp, isOverdue, formatOverdueTime, onSuccess }, ref) => {
  const { user } = useAuth();
  const status = getLeadStatus(followUp.leadStatus);
  const queryClient = useQueryClient();
  const completeMutation = useMutation({
    mutationFn: () => followUpsAPI.complete(followUp.id),
    onSuccess: async () => {
      await Promise.all(['dashboard', 'lead', 'leads', 'inbox', 'followUps', 'activity'].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key], refetchType: key === 'dashboard' ? 'all' : 'active' })
      ));
      onSuccess?.('Task completed.');
    },
  });

  return (
    <div
      ref={ref}
      tabIndex={-1}
      className={`group rounded-xl p-3 border transition-all duration-200 hover:shadow-md ${
        isOverdue 
          ? 'bg-white border-red-200 shadow-sm'
          : 'bg-white border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h3 className="min-w-0 font-semibold text-gray-900 text-base break-words">
              {followUp.leadName}
            </h3>
            {isOverdue && (
              <span className="bg-red-100 text-red-800 text-xs px-2.5 py-1 rounded-full font-semibold shadow-sm">
                {formatOverdueTime(followUp.scheduledTime)} overdue
              </span>
            )}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <div className="flex items-center gap-1.5 text-gray-600">
              <Clock className="w-3.5 h-3.5" />
              <span className="font-medium">{(isOverdue || followUp.completed) ? formatFollowUp(followUp) : followUp.dateOnly ? 'No set time' : followUp.time?.slice(0, 5)}</span>
            </div>
            <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold border border-transparent ${status.className}`}>
              {status.label}
            </span>
          </div>

          </div>

          <TaskSource task={followUp}/>
          {followUp.note && (
            <div className="mt-1">
              <p className="text-sm text-gray-700 leading-snug break-words">
                {followUp.note}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2">
        {followUp.completed && <p className="mr-auto text-xs font-medium text-green-700">Completed</p>}
        {followUp.whatsappUrl && (<a
          href={followUp.whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open WhatsApp"
          className={`min-h-[44px] flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-sm font-medium ${followUp.completed ? 'text-green-700 hover:bg-green-50' : 'flex-1 bg-green-700 text-white hover:bg-green-800'}`}
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp
        </a>)}

        {!followUp.completed && can(user, 'manageFollowUps') && (
          <button type="button" disabled={completeMutation.isPending} onClick={() => completeMutation.mutate()}
            className="min-h-[44px] flex-1 px-2 py-2 rounded-lg bg-primary-600 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
            {completeMutation.isPending ? 'Updating...' : 'Mark Done'}
          </button>
        )}
        <Link
          to={`/leads/${followUp.leadId}`}
          aria-label={`View ${followUp.leadName}`}
          className="flex h-11 w-11 shrink-0 items-center justify-center bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 hover:text-gray-900 active:bg-gray-300 transition-all"
        >
          <ChevronRight className="w-5 h-5" />
        </Link>
      </div>
      {completeMutation.isError && <FormError message={completeMutation.error?.response?.data?.error || 'Could not complete task. Please try again.'} />}
    </div>
  );
});

export default TodayDashboard;
